/**
 * Combat system: target acquisition, attacking on cooldown, damage + death.
 * Ranged attacks emit a cosmetic projectile event (renderer draws the arc;
 * damage is applied instantly for determinism simplicity).
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import type { Eid, Vec2 } from "../types";
import { FIXED_DT } from "../types";
import { findPath } from "../pathfinding";
import { advanceOrder, repathToDoor, setOrder } from "../orders";
import { canGatherFrom } from "../economy";
import { isNaval, domainOf } from "../naval";

/** A wounded-but-alive animal reacts: boars charge their attacker, everything
 *  else flees away from where the hit came from. */
function provoke(world: World, prey: Eid, attacker: Eid, atkPos: Vec2): void {
  const f = world.fauna.get(prey);
  if (!f) return;
  const pmv = world.move.get(prey);
  if (!pmv) return;
  if (f.aggressive && world.combat.has(prey)) {
    pmv.intent = { kind: "attack", target: attacker }; // charge!
  } else {
    f.fleeFor = 3.5;
    f.fleeFromX = atkPos.x;
    f.fleeFromZ = atkPos.y;
    pmv.path = []; // faunaSystem plots a fresh escape route next tick
  }
}

/** A struck military unit FIGHTS BACK: an IDLE one charges its attacker (even a
 *  ranged shooter outside its own vision — you know who hit you). It commits to
 *  that one target until it dies, then re-acquires — no target thrash. A unit
 *  already under orders (attacking / gathering / moving) is left alone. The guard
 *  post is stamped so the counter-charge is leashed (won't chase across the map). */
function retaliate(world: World, victim: Eid, attacker: Eid): void {
  if (world.fauna.has(victim)) return; // wildlife handled by provoke()
  const vmv = world.move.get(victim);
  const vid = world.ident.get(victim);
  if (!vmv || !world.combat.has(victim) || vid?.cls === "worker") return;
  if (vmv.intent !== undefined) return; // busy (ordered/already fighting) — don't yank it off
  const vtr = world.transform.get(victim);
  if (!vmv.guard && vtr) vmv.guard = { ...vtr.pos };
  vmv.intent = { kind: "attack", target: attacker };
}

/** Auto-acquire / chase radius for an idle military unit's DEFENSIVE engagement:
 *  its line-of-sight (so it charges anything it can SEE, not just point-blank). */
function visionRange(world: World, eid: Eid, combat: { range: number }): number {
  return world.los.get(eid)?.los ?? combat.range + world.size * 3;
}

function repath(world: World, eid: number, tx: number, ty: number) {
  const tr = world.transform.get(eid);
  const mv = world.move.get(eid);
  if (!tr || !mv) return;
  mv.path = findPath(world, tr.pos.x, tr.pos.y, tx, ty, 6000, domainOf(world, eid), world.owner.get(eid)?.player, false, tr.radius) ?? [];
}

/** Seconds of NO progress toward a target before a chasing unit breaks off. */
const CHASE_GIVE_UP = 3.5;

/** Track pursuit progress and DISENGAGE from a target the unit can't get closer
 *  to (it fled onto water we can't follow, or simply outran us). Returns true
 *  when it gave up — intent cleared, sent back to the guard post if defensive,
 *  else left idle where it stands. A unit that's still CLOSING (distance shrinking)
 *  resets the timer, so a legit chase of a reachable foe never trips this; static
 *  targets (buildings) keep closing as the unit approaches, so they never do either. */
function chaseStuck(
  world: World,
  eid: Eid,
  combat: { chaseTarget?: Eid; chaseBest?: number; chaseTimer?: number },
  mv: { intent?: unknown; guard?: Vec2; path: Vec2[] },
  target: Eid,
  dist: number
): boolean {
  // (re)start the tracker when we pick up a new target
  if (combat.chaseTarget !== target) {
    combat.chaseTarget = target;
    combat.chaseBest = dist;
    combat.chaseTimer = 0;
    return false;
  }
  const STEP = world.size * 0.05; // "made progress" if we closed at least this much
  if (dist < (combat.chaseBest ?? Infinity) - STEP) {
    combat.chaseBest = dist;
    combat.chaseTimer = 0;
    return false;
  }
  combat.chaseTimer = (combat.chaseTimer ?? 0) + FIXED_DT;
  if (combat.chaseTimer < CHASE_GIVE_UP) return false;
  // stuck: drop the target.
  combat.chaseTarget = undefined;
  combat.chaseBest = undefined;
  combat.chaseTimer = 0;
  (mv as { intent?: unknown }).intent = undefined;
  if (mv.guard) repath(world, eid, mv.guard.x, mv.guard.y);
  else mv.path = [];
  return true;
}

export function isEnemy(world: World, a: number, b: number): boolean {
  const oa = world.owner.get(a)?.player;
  const ob = world.owner.get(b)?.player;
  if (oa === undefined || ob === undefined || oa === ob) return false;
  // allies (same team) are NOT enemies; only different teams fight.
  return world.players.get(oa)?.team !== world.players.get(ob)?.team;
}

/** Reused candidate buffer (single-threaded; nearestEnemyInRange isn't re-entrant). */
const NEAR: Eid[] = [];

export function nearestEnemyInRange(
  world: World,
  eid: number,
  range: number
): Eid | null {
  const tr = world.transform.get(eid);
  if (!tr) return null;
  let best: Eid | null = null;
  let bestD = range * range;
  // candidates from the spatial index (a superset within `range`); the exact
  // distance test + lower-eid tie-break below make the result order-independent.
  NEAR.length = 0;
  world.spatial.queryInto(tr.pos.x, tr.pos.y, range, NEAR);
  for (const other of NEAR) {
    if (other === eid) continue;
    if ((world.health.get(other)?.hp ?? 0) <= 0) continue;
    if (!isEnemy(world, eid, other)) continue;
    const oTr = world.transform.get(other)!;
    const d = (oTr.pos.x - tr.pos.x) ** 2 + (oTr.pos.y - tr.pos.y) ** 2;
    if (d < bestD || (d === bestD && other < (best ?? Infinity))) {
      bestD = d;
      best = other;
    }
  }
  return best;
}

export function combatSystem(world: World, _reg: Registry): void {
  world.rebuildSpatial(); // index current positions for target acquisition
  for (const [eid, combat] of world.combat) {
    const mv = world.move.get(eid);
    const tr = world.transform.get(eid);
    // Some lightweight combat fixtures/entities omit Health; only skip actors
    // whose health component explicitly says they are dead.
    if (!mv || !tr || (world.health.get(eid)?.hp ?? 1) <= 0) continue;
    if (combat.cooldownLeft > 0)
      combat.cooldownLeft = Math.max(0, combat.cooldownLeft - FIXED_DT);

    // resolve target from intent
    let target: Eid | null = null;
    if (mv.intent?.kind === "attack") {
      const t = mv.intent.target;
      // a target is valid if it's an enemy OR involves wildlife (a unit hunting
      // an animal, or a provoked boar charging its attacker).
      if ((world.health.get(t)?.hp ?? 0) > 0 && (isEnemy(world, eid, t) || world.fauna.has(t) || world.fauna.has(eid))) target = t;
      else {
        // the quarry died — a hunter that can gather automatically harvests the
        // meat carcass it left (no need to re-order the workers).
        if (world.gatherer.has(eid) && world.resource.has(t) && canGatherFrom(world, t, eid)) {
          setOrder(world, eid, { kind: "gather", node: t }, false);
        } else if (!advanceOrder(world, eid)) {
          mv.intent = undefined;
        }
        continue;
      }
    } else if (mv.intent?.kind === "attackMove") {
      target = nearestEnemyInRange(world, eid, combat.range + 0.5);
    } else if (!mv.intent) {
      // wildlife never auto-aggros — it only reacts when attacked (faunaSystem
      // wanders it). Skip the defensive acquire below.
      if (world.fauna.has(eid)) continue;
      // Idle DEFENSIVE auto-acquire (military only): charge any enemy within
      // VISION (line-of-sight) — so melee troops actually engage foes they can
      // SEE, not just ones already on top of them. A leash from the home post
      // (a bit past vision) breaks off a chase so they don't conga across the map;
      // the deterministic nearest-enemy pick makes them commit to one target,
      // kill it, then take the next (no thrash in a 3-v-1).
      const id = world.ident.get(eid);
      if (id && id.cls !== "worker") {
        if (!mv.guard) mv.guard = { ...tr.pos };
        const homeD = Math.hypot(tr.pos.x - mv.guard.x, tr.pos.y - mv.guard.y);
        const vision = visionRange(world, eid, combat);
        const leash = vision + world.size * 3;
        if (homeD < leash) target = nearestEnemyInRange(world, eid, vision);
        // COMMIT to the acquired foe via an attack intent — exactly like a player
        // order — so it's exempted from that target's collision footprint (a melee
        // unit can otherwise "arrive" a tile short of a BUILDING's inflated repel
        // tolerance and never land a hit). The guard stamp keeps it leashed; when
        // the target dies the intent clears and it takes the next.
        if (target !== null) mv.intent = { kind: "attack", target };
        if (target === null) {
          // nothing to fight (or strayed too far) → return to / hold the post
          if (homeD > world.size * 0.7) {
            if (mv.path.length === 0) repath(world, eid, mv.guard.x, mv.guard.y);
          } else {
            mv.path = [];
          }
          continue;
        }
      }
    }

    if (target === null) {
      // for attackMove with no enemy in range, keep moving toward dest
      if (mv.intent?.kind === "attackMove" && mv.path.length === 0) {
        repath(world, eid, mv.intent.x, mv.intent.y);
      }
      continue;
    }

    const tTr = world.transform.get(target);
    const tHealth = world.health.get(target);
    if (!tTr || !tHealth || tHealth.hp <= 0) {
      if (!advanceOrder(world, eid)) mv.intent = undefined;
      continue;
    }
    // Leash a DEFENSIVE/retaliation attack (guard post stamped; a player-ordered
    // attack clears the guard so it's never leashed): break off and return home if
    // we've chased a fleeing foe too far past where we started. Prevents a counter-
    // charge from dragging a unit across the whole map.
    if (mv.guard && mv.intent?.kind === "attack" && !world.fauna.has(eid)) {
      const homeD = Math.hypot(tr.pos.x - mv.guard.x, tr.pos.y - mv.guard.y);
      if (homeD > visionRange(world, eid, combat) + world.size * 4) {
        mv.intent = undefined;
        repath(world, eid, mv.guard.x, mv.guard.y);
        continue;
      }
    }
    const myR = tr.radius;
    const tR = tTr.radius;
    const dist = Math.hypot(tTr.pos.x - tr.pos.x, tTr.pos.y - tr.pos.y);
    // Hunting reach: a unit jabbing/throwing a spear at WILDLIFE strikes from
    // ~1 tile (≈10 m) away, so a slow worker can actually land hits on a fleeing
    // animal instead of never catching it. Unit-vs-unit melee is unchanged.
    const hunting = world.fauna.has(target);
    const effRange = hunting ? Math.max(combat.range, Math.sqrt(3) * world.size) : combat.range;
    const reach = effRange + myR + tR;

    if (dist <= reach) {
      // in range — stop & face. We can hit it, so clear any chase frustration
      // (if it later runs out of reach again, the grace restarts from here).
      combat.chaseTarget = undefined;
      mv.path = [];
      tr.facing = Math.atan2(tTr.pos.y - tr.pos.y, tTr.pos.x - tr.pos.x);
      if (combat.cooldownLeft <= 0) {
        const tId = world.ident.get(target);
        const cls = tId?.cls;
        let dmg = combat.damage;
        const bonus = cls ? combat.bonus?.[cls] : undefined;
        if (bonus) dmg *= bonus;
        // cannon ships: a flat siege multiplier vs BUILDINGS (which carry no
        // EntityClass, so the class-bonus table can't target them).
        if (combat.bonusBuilding && world.building.has(target)) dmg *= combat.bonusBuilding;
        const applied = Math.max(1, dmg - tHealth.armor);
        tHealth.hp -= applied;
        combat.cooldownLeft = combat.cooldown;
        const owner = world.owner.get(eid)?.player ?? 0;
        const color = world.players.get(owner)?.color ?? "#fff";
        // draw a flying shaft for true ranged attacks AND for spear-jab hunting
        // (a melee unit reaching a tile to a fleeing animal). Naval guns throw a
        // heavier projectile (bolt/shell) so the renderer can arc them differently.
        if (combat.range > 1.5 || (hunting && dist > myR + tR + world.size * 0.3)) {
          const aCls = world.ident.get(eid)?.cls;
          const pkind = aCls === "naval" ? (combat.damage >= 20 ? "shell" : "bolt") : "arrow";
          world.emit({ type: "projectile", tick: 0, from: { ...tr.pos }, to: { ...tTr.pos }, color, kind: pkind });
        }
        world.emit({ type: "hit", tick: 0, at: { ...tTr.pos } });
        if (tHealth.hp <= 0) {
          // a killed animal becomes a gatherable meat carcass in place; anything
          // else is removed outright.
          const meat = world.toCarcass(target);
          if (!meat) world.markDead(target);
          world.emit({ type: "death", tick: 0, at: { ...tTr.pos } });
          // the hunter that landed the kill starts gathering its meat right away
          if (meat && world.gatherer.has(eid) && canGatherFrom(world, target, eid)) {
            setOrder(world, eid, { kind: "gather", node: target }, false);
          } else if (!advanceOrder(world, eid)) {
            mv.intent = undefined;
          }
        } else {
          provoke(world, target, eid, tr.pos);   // wildlife flees / boar charges
          retaliate(world, target, eid);          // struck troops fight back
        }
      }
    } else {
      // out of range — pursue, but BREAK OFF if we can't make progress toward the
      // target (it sailed onto open water we can't follow, slipped out of vision,
      // or outran us): stop waiting forever and return to the post / go idle.
      if (chaseStuck(world, eid, combat, mv, target, dist)) continue;
      // close in. A building blocks its hex, so step to the wall.
      // WILDLIFE flees, so repath every tick toward the animal's CURRENT spot
      // (tight pursuit) — otherwise the hunter keeps walking to where it already
      // left and never catches up.
      if (world.building.has(target)) {
        if (mv.path.length === 0) {
          if (isNaval(world, eid)) {
            // a warship can't pull up to the wall — close to the nearest open water
            // beside the building (naval pathing snaps the goal to the coast there)
            // and let its long guns reach over the shore.
            repath(world, eid, tTr.pos.x, tTr.pos.y);
          } else {
            // Continuous pathfinding resolves the blocked centre to a model-clear
            // contact point even when already adjacent; never append a straight
            // centreward step that cuts back through the building texture.
            repathToDoor(world, eid, target);
          }
        }
      } else if (hunting || mv.path.length === 0) {
        repath(world, eid, tTr.pos.x, tTr.pos.y);
      }
    }
  }
}
